// /app/frontend/src/components/SiteNavigation.jsx
// Consistent navigation component for all pages
import { useNavigate, Link } from "react-router-dom";
import { Home, Search, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const SiteNavigation = ({ user, transparent = false }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navClass = transparent 
    ? "fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100"
    : "sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100";

  return (
    <nav className={navClass} data-testid="site-navigation">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#E07C4D] to-[#D16844] rounded-xl flex items-center justify-center shadow-lg">
              <Home className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Its-Sold.com<sup className="text-xs">™</sup>
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/listings"
              className="text-gray-600 hover:text-[#E07C4D] font-medium transition-colors"
            >
              Browse Homes
            </Link>
            <Link
              to="/history"
              className="text-gray-600 hover:text-[#E07C4D] font-medium transition-colors"
            >
              Our Story
            </Link>
            <Link
              to="/blog"
              className="text-gray-600 hover:text-[#E07C4D] font-medium transition-colors"
            >
              Blog
            </Link>
          </div>

          {/* Auth Buttons */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="ghost"
                  className="text-gray-700 hover:text-[#E07C4D] font-medium hidden sm:inline-flex"
                >
                  Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/create-listing')}
                  className="bg-gradient-to-r from-[#E07C4D] to-[#D16844] hover:from-[#D16844] hover:to-[#B55D35] text-white font-semibold rounded-full px-6 shadow-md hover:shadow-lg transition-all"
                >
                  <span className="hidden sm:inline">+ New Listing</span>
                  <span className="sm:hidden">+ List</span>
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  className="text-gray-700 hover:text-[#E07C4D] font-medium hidden sm:inline-flex"
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/register?role=seller')}
                  className="bg-gradient-to-r from-[#E07C4D] to-[#D16844] hover:from-[#D16844] hover:to-[#B55D35] text-white font-semibold rounded-full px-6 shadow-md hover:shadow-lg transition-all"
                  data-testid="nav-list-free-btn"
                >
                  <span className="hidden sm:inline">List Free</span>
                  <span className="sm:hidden">List</span>
                </Button>
              </>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 text-gray-600 hover:text-[#E07C4D]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-100 pt-4">
            <div className="flex flex-col gap-3">
              <Link
                to="/listings"
                className="text-gray-700 hover:text-[#E07C4D] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Browse Homes
              </Link>
              <Link
                to="/history"
                className="text-gray-700 hover:text-[#E07C4D] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Our Story
              </Link>
              <Link
                to="/blog"
                className="text-gray-700 hover:text-[#E07C4D] font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </Link>
              {!user && (
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-[#E07C4D] font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default SiteNavigation;
